package com.flowyride.data.speedcameras.datagouv

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.SpeedCameraType
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

// Columns of the official CSV. Header cells carry stray spaces ("VMA ", " Longitude"):
// they are looked up whitespace- and case-insensitively.
private const val COLUMN_ID = "numéro de radar"
private const val COLUMN_TYPE = "type de radar"
private const val COLUMN_SPEED_LIMIT = "vma"
private const val COLUMN_LATITUDE = "latitude"
private const val COLUMN_LONGITUDE = "longitude"

/** @return null for rows without a usable position. */
internal fun JsonObject.toSpeedCameraOrNull(): SpeedCamera? {
    val columns = entries.associate { (key, value) -> key.trim().lowercase() to (value as? JsonPrimitive)?.content }
    val latitude = columns[COLUMN_LATITUDE]?.toCoordinate() ?: return null
    val longitude = columns[COLUMN_LONGITUDE]?.toCoordinate() ?: return null
    val point = runCatching { GeoPoint(latitude, longitude) }.getOrNull() ?: return null
    return SpeedCamera(
        id = columns[COLUMN_ID] ?: "${point.latitude},${point.longitude}",
        point = point,
        type = columns[COLUMN_TYPE].toSpeedCameraType(),
        speedLimitKmh = columns[COLUMN_SPEED_LIMIT]?.toSpeedLimit(),
    )
}

/**
 * "80" → 80. Several speeds ("80/90", "70 - 90": e.g. cars and trucks) → the highest: a motorcycle
 * gets the car limit, never the lower truck one.
 */
private fun String.toSpeedLimit(): Int? = SPEED.findAll(this).mapNotNull { it.value.toIntOrNull() }.maxOrNull()

private val SPEED = Regex("\\d{2,3}")

/** "+45.361" or "45,361" → 45.361 */
private fun String.toCoordinate(): Double? = trim().removePrefix("+").replace(',', '.').toDoubleOrNull()

private fun String?.toSpeedCameraType(): SpeedCameraType = when (this?.trim()?.uppercase()) {
    "ETF" -> SpeedCameraType.FIXED
    "ETT" -> SpeedCameraType.TOWER
    "ETD" -> SpeedCameraType.DISCRIMINATING
    "ETVM" -> SpeedCameraType.SECTION
    "ETFR" -> SpeedCameraType.RED_LIGHT
    "ETPN" -> SpeedCameraType.LEVEL_CROSSING
    "ETU" -> SpeedCameraType.URBAN
    else -> SpeedCameraType.UNKNOWN
}
